/**
 * /dispatch/admin/at-risk-customers — operator UI for the
 * address-shape cohort.
 *
 * Lists customers with structured-object addresses (the cohort that was
 * silently crashed on Place Order before the 2026-02-19 hotfix). Offers
 * a one-tap "Send apology" email through the shared SendDirectEmailModal,
 * pre-selecting the apology-bug template.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { SendDirectEmailModal } from '../../components/SendDirectEmailModal';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function AtRiskCustomersPage() {
  const [data, setData] = useState(null);
  const [objObjData, setObjObjData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCustomer, setOpenCustomer] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [shape, objObj] = await Promise.all([
        axios.get(`${API_URL}/api/admin/data-quality/address-shape`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/data-quality/object-object-address`, { withCredentials: true }),
      ]);
      setData(shape.data);
      setObjObjData(objObj.data);
    } catch (err) {
      toast.error('Failed to load — check that you are logged in as super-admin');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#050003] text-[#A3C7A8] font-mono p-6" data-testid="at-risk-page">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl text-[#39FF14] uppercase tracking-widest mb-1">// AT-RISK CUSTOMERS</h1>
        <p className="text-xs text-[#82937B] mb-6">
          Customers with structured-object addresses — were silently crashed on Place Order before the 2026-02-19 hotfix.
        </p>

        {loading ? (
          <div className="text-[#82937B] text-xs">Loading…</div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Total customers', value: data.summary.total_customers, color: '#A3C7A8' },
                { label: 'Legacy (safe)', value: data.summary.legacy_string_count, color: '#39FF14' },
                { label: 'At risk', value: data.summary.structured_object_count, color: '#D4823B' },
                { label: 'No address', value: data.summary.no_address_count, color: '#82937B' },
              ].map((s) => (
                <div key={s.label} className="border border-[#39FF14]/15 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#82937B]">{s.label}</div>
                  <div className="text-2xl mt-1" style={{ color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>

            {data.at_risk_customers.length === 0 ? (
              <div className="border border-[#39FF14]/30 p-6 text-center">
                <CheckCircle2 className="mx-auto text-[#39FF14] mb-2" size={32} />
                <p className="text-[#A3C7A8]">All clear — no customers currently at risk.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {data.at_risk_customers.map((c) => (
                  <div
                    key={c.id}
                    className="border border-[#D4823B]/30 bg-[#D4823B]/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                    data-testid={`at-risk-row-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/dispatch/admin/users?openUser=${c.id}&from=at-risk`}
                          className="text-white text-sm hover:text-[#39FF14] hover:underline"
                          data-testid={`at-risk-customer-link-${c.id}`}
                        >
                          {c.name || '(no name)'}
                        </Link>
                        <span className="text-[#82937B] text-xs">{c.email}</span>
                        {c.outreach_sent && (
                          <span className="text-[10px] bg-[#39FF14]/15 text-[#39FF14] px-2 py-0.5 uppercase tracking-widest">
                            Outreach sent
                          </span>
                        )}
                      </div>
                      <div className="text-[#82937B] text-[11px] truncate mt-1">
                        {[c.address?.line1, c.address?.city, c.address?.state, c.address?.postcode].filter(Boolean).join(', ')}
                      </div>
                      <div className="text-[10px] text-[#82937B]/70 mt-1 uppercase tracking-widest">
                        {(() => {
                          const joined = c.created_at ? String(c.created_at).slice(0, 10) : '—';
                          const oc = Number(c.order_count || 0);
                          const lastRaw = c.last_order_at ? String(c.last_order_at).slice(0, 10) : null;
                          // Feb 2026 — surface real order footprint so operators
                          // don't wrongly assume "never ordered" for customers
                          // who actually have delivered orders (the previous
                          // `users.last_order_at` field was never written).
                          const lastLabel = oc > 0
                            ? `${oc} order${oc === 1 ? '' : 's'}${lastRaw ? ` · last ${lastRaw}` : ''}`
                            : 'never';
                          return `Joined ${joined} · ${lastLabel}`;
                        })()}
                      </div>
                    </div>
                    <button
                      onClick={() => setOpenCustomer(c)}
                      className="px-3 py-2 text-xs uppercase tracking-widest border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#050505] flex items-center gap-2 shrink-0"
                      data-testid={`at-risk-send-${c.id}`}
                    >
                      <Mail size={14} />
                      {c.outreach_sent ? 'Send again' : 'Send apology'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-[#D4823B] flex items-center gap-2"><AlertCircle size={16} /> Could not load.</div>
        )}
      </div>

      {openCustomer && (
        <SendDirectEmailModal
          customer={openCustomer}
          title="SEND APOLOGY"
          onClose={() => setOpenCustomer(null)}
          onSent={() => load()}
        />
      )}

      {/* Feb 2026 — Secondary cohort: profile-level `[object Object]` string.
          Caused by the SA drawer coercion bug (now patched). These orders are
          not blocked; they just see the "address incomplete" nudge every
          checkout until someone repairs their profile field. */}
      {objObjData && (
        <div className="max-w-5xl mx-auto mt-10" data-testid="obj-obj-section">
          <h2 className="text-xl text-[#D4823B] uppercase tracking-widest mb-1">
            {'// "[OBJECT OBJECT]" ADDRESS COHORT'}
          </h2>
          <p className="text-xs text-[#82937B] mb-4">
            {'Profile `address` field holds the "[object Object]" sentinel string'}
            {' '}(React coercion artefact from the pre-fix SA drawer). Checkout is
            NOT blocked — customers see a re-pick nudge and their saved
            addresses are untouched.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <div className="border border-[#D4823B]/30 p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#82937B]">Corrupted rows</div>
              <div className="text-2xl mt-1 text-[#D4823B]">{objObjData.count}</div>
            </div>
            <div className="border border-[#39FF14]/15 p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#82937B]">Have saved addresses</div>
              <div className="text-2xl mt-1 text-[#39FF14]">
                {objObjData.customers.filter((c) => c.has_saved_addresses).length}
              </div>
            </div>
            <div className="border border-[#82937B]/30 p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#82937B]">No saved backup</div>
              <div className="text-2xl mt-1 text-[#A3C7A8]">
                {objObjData.customers.filter((c) => !c.has_saved_addresses).length}
              </div>
            </div>
          </div>

          {objObjData.count === 0 ? (
            <div className="border border-[#39FF14]/30 p-6 text-center" data-testid="obj-obj-empty">
              <CheckCircle2 className="mx-auto text-[#39FF14] mb-2" size={32} />
              <p className="text-[#A3C7A8]">No corrupted profile addresses.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {objObjData.customers.map((c) => (
                <div
                  key={c.id}
                  className="border border-[#D4823B]/30 bg-[#D4823B]/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid={`obj-obj-row-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/dispatch/admin/users?openUser=${c.id}&from=at-risk`}
                        className="text-white text-sm hover:text-[#39FF14] hover:underline"
                        data-testid={`obj-obj-link-${c.id}`}
                      >
                        {c.name || '(no name)'}
                      </Link>
                      <span className="text-[#82937B] text-xs">{c.email}</span>
                      {c.has_saved_addresses ? (
                        <span className="text-[10px] bg-[#39FF14]/15 text-[#39FF14] px-2 py-0.5 uppercase tracking-widest">
                          {c.saved_addresses_count} saved
                        </span>
                      ) : (
                        <span className="text-[10px] bg-[#D4823B]/15 text-[#D4823B] px-2 py-0.5 uppercase tracking-widest">
                          No backup
                        </span>
                      )}
                    </div>
                    <div className="text-[#D4823B] text-[11px] font-mono mt-1">
                      {`address: "${c.address}"`}
                    </div>
                    <div className="text-[10px] text-[#82937B]/70 mt-1 uppercase tracking-widest">
                      {(() => {
                        const joined = typeof c.created_at === 'string' ? c.created_at.slice(0, 10) : '—';
                        const oc = Number(c.order_count || 0);
                        const lastRaw = typeof c.last_order_at === 'string' ? c.last_order_at.slice(0, 10) : null;
                        const lastLabel = oc > 0
                          ? `${oc} order${oc === 1 ? '' : 's'}${lastRaw ? ` · last ${lastRaw}` : ''}`
                          : 'never';
                        return `Joined ${joined} · ${lastLabel}`;
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
